import { AppConfig } from "../AppConfig";
import {
  buildTableDataPayload,
  IAnalysisFrameworkReport,
  ILicenseLogEntry,
  ITableDataResponse,
  LicenseLogMeta,
} from "../shared/model/ILicenseLogEntry";
import { RESTApi } from "./core/RESTApi";

const extractTableRows = (data: ITableDataResponse | ILicenseLogEntry[]): ILicenseLogEntry[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (!Array.isArray(data.rows)) {
    return [];
  }

  const rows = data.rows as any[];

  const possibleHeaders = (data as any).columns || (data as any).headers || (data as any).columnNames || null;
  if (Array.isArray(rows[0]) && Array.isArray(possibleHeaders) && possibleHeaders.length > 0) {
    return rows.map((r) => {
      const obj: ILicenseLogEntry = {};
      possibleHeaders.forEach((h: string, i: number) => {
        obj[h] = r[i] ?? "";
      });
      return obj;
    });
  }

  if (Array.isArray(rows[0]) && rows.length > 1 && rows[0].every((v: any) => typeof v === "string")) {
    const headers = rows[0] as string[];
    return rows.slice(1).map((r) => {
      const obj: ILicenseLogEntry = {};
      headers.forEach((h, i) => {
        obj[h] = r[i] ?? "";
      });
      return obj;
    });
  }

  return rows as ILicenseLogEntry[];
};

export interface ISyncPH1Data {
  projects: ILicenseLogEntry[];
  skippedProjects: ILicenseLogEntry[];
}

export interface IUpdateSyncData extends ISyncPH1Data {
  successMessage?: string;
}

export interface IBupaCustomerDetails {
  BUPA_AND_CUST_ID?: string;
  CUSTOMER_ID?: string;
  CUSTOMER_NAME?: string;
}

export interface ICheckedProductDetails {
  PRODUCTS_ID?: string;
  LIC_INFO?: Array<{ NAME?: string; VALUE?: unknown }>;
}

export interface IMaintainedProjectDetails {
  PROJECT_ID?: string;
  INACT_STAT?: Array<{ STAT?: string; INACT?: string }>;
  PRART_STUFE?: Array<{
    PRART?: string;
    STUFE?: string | number;
    INACT?: string;
    STAT?: string;
  }>;
  CHECK_PROD?: ICheckedProductDetails[] | { PRODUCTS?: string };
  BUPA_CUST?: IBupaCustomerDetails | IBupaCustomerDetails[];
}

const normalizePH1Data = (data: any): ISyncPH1Data => {
  let entries: any[] = [];

  if (Array.isArray(data)) {
    entries = data;
  } else if (Array.isArray(data?.rows)) {
    entries = data.rows;
  } else if (Array.isArray(data?.data)) {
    entries = data.data;
  } else if (data !== null && typeof data === "object") {
    entries = [data];
  } else {
    entries = [data];
  }

  const skippedProjects: ILicenseLogEntry[] = [];
  const projects = entries.map((entry) => {
    if (entry === null || entry === undefined) {
      return {};
    }
    if (typeof entry !== "object") {
      return { value: entry };
    }
    const normalized: ILicenseLogEntry = {};
    Object.keys(entry).forEach((k) => {
      const v = entry[k];
      if (k === "SKIPPED_PROJECT") {
        if (Array.isArray(v)) {
          skippedProjects.push(...v);
        }
        return;
      }
      if (v === null || v === undefined) {
        normalized[k] = "";
      } else if (typeof v === "object") {
        try {
          normalized[k] = JSON.stringify(v);
        } catch (e) {
          normalized[k] = String(v);
        }
      } else {
        normalized[k] = v as string | number | boolean;
      }
    });
    return normalized;
  });

  return {
    projects,
    skippedProjects: skippedProjects.map((entry) => ({
      PROJECT_ID_SKIP: entry.PROJECT_ID_SKIP ?? "",
      DESCRIPTION: entry.DESCRIPTION ?? "",
    })),
  };
};

const normalizeUpdateSyncData = (data: any): IUpdateSyncData => {
  if (typeof data === "string") {
    return {
      projects: [],
      skippedProjects: [],
      successMessage: data,
    };
  }

  const isTableResponse =
    Array.isArray(data) ||
    Array.isArray(data?.rows) ||
    Array.isArray(data?.data);
  const successMessage =
    !isTableResponse && data && typeof data === "object"
      ? data.MESSAGE ?? data.message
      : undefined;

  return {
    ...normalizePH1Data(data),
    ...(typeof successMessage === "string" && successMessage.length > 0
      ? { successMessage }
      : {}),
  };
};

class AdminViewApiDefault extends RESTApi {
  private buildAdminViewParams(
    source: string,
    additionalParams: Record<string, string> = {}
  ): string {
    const params = new URLSearchParams({
      source,
      ...additionalParams,
    });

    return `${AppConfig.BACKEND_HOST}${LicenseLogMeta.path}?${params.toString()}`;
  }

  private getTableData(
    source: string,
    additionalParams: Record<string, string> = {}
  ): Promise<ILicenseLogEntry[]> {
    return this.get<ITableDataResponse | ILicenseLogEntry[]>(
      this.buildAdminViewParams(source, additionalParams)
    ).then(extractTableRows);
  }

  getLicenseLog(year: number): Promise<ILicenseLogEntry[]> {
    return this.getTableData("license_log");
  }

  getDownloadHistory(): Promise<ILicenseLogEntry[]> {
    return this.getTableData("download_history");
  }

  getProjectList(): Promise<ILicenseLogEntry[]> {
    return this.getTableData("projects");
  }

  getProductMapping(): Promise<ILicenseLogEntry[]> {
    return this.getTableData("product_mapping");
  }

  getCustomerList(): Promise<ILicenseLogEntry[]> {
    return this.getTableData("customer");
  }

  getAllReleases(): Promise<ILicenseLogEntry[]> {
    return this.getTableData("releases");
  }

  getReleases(productId: string): Promise<ILicenseLogEntry[]> {
    return this.getTableData("releases", {
      product: productId,
    });
  }

  getSwitchTable(token?: string): Promise<ILicenseLogEntry[]> {
    const additionalParams: Record<string, string> = token ? { token } : {};
    return this.getTableData("switch", additionalParams).then((rows) =>
      rows.map((row) => {
        const normalized: ILicenseLogEntry = {};
        Object.keys(row).forEach((k) => {
          const v = row[k];
          if (v === null || v === undefined) {
            normalized[k] = "";
          } else if (typeof v === "object") {
            try {
              normalized[k] = JSON.stringify(v);
            } catch (e) {
              normalized[k] = String(v);
            }
          } else {
            normalized[k] = v as string | number | boolean;
          }
        });
        return normalized;
      })
    );
  }

  /**
   * Triggers the PH1 data sync for all projects. RESTApi adds the current
   */
  private buildProjectIdUrlValue(projectId?: string): string {
    const trimmedProjectId = projectId?.trim() ?? "";
    return trimmedProjectId ? encodeURIComponent(trimmedProjectId) : "%20";
  }

  syncPH1Data(projectId?: string): Promise<ISyncPH1Data> {
    const projectIdUrlValue = this.buildProjectIdUrlValue(projectId);
    const url = `${AppConfig.BACKEND_HOST}/sync_ph1_data/${projectIdUrlValue}/`;

    return this.get<any>(url).then(normalizePH1Data);
  }

  syncSkippedProjects(projectId?: string): Promise<ILicenseLogEntry[]> {
    const projectIdUrlValue = this.buildProjectIdUrlValue(projectId);
    const url = `${AppConfig.BACKEND_HOST}/sync_skipped_proj/${projectIdUrlValue}`;

    return this.get<any>(url).then((response) => {
      const normalized = normalizePH1Data(response);
      return normalized.skippedProjects.length > 0
        ? normalized.skippedProjects
        : normalized.projects;
    });
  }

  getSkipReasons(projectId?: string): Promise<IMaintainedProjectDetails[]> {
    const projectIdUrlValue = this.buildProjectIdUrlValue(projectId);
    const url = `${AppConfig.BACKEND_HOST}/get_reason_skip/${projectIdUrlValue}`;

    return this.get<any>(url).then((response) => {
      const data = response?.data ?? response;
      const rows = [data, data?.rows, data?.data].find(Array.isArray);
      if (rows) {
        return rows as IMaintainedProjectDetails[];
      }

      return data && typeof data === "object"
        ? [data as IMaintainedProjectDetails]
        : [];
    });
  }

  private buildUpdateSyncPathSegment(checked: boolean): string {
    return checked ? "X" : "%20";
  }

  private buildUpdateSyncUrl(
    projectId: string | undefined,
    options: {
      email: boolean;
      simulationMode: boolean;
      showResults: boolean;
    }
  ): string {
    const projectIdUrlValue = this.buildProjectIdUrlValue(projectId);

    return `${AppConfig.BACKEND_HOST}/sync/${projectIdUrlValue}/${this.buildUpdateSyncPathSegment(
      options.email
    )}/${this.buildUpdateSyncPathSegment(options.simulationMode)}/${this.buildUpdateSyncPathSegment(
      options.showResults
    )}`;
  }

  updateSync(
    projectId: string | undefined,
    options: {
      email: boolean;
      simulationMode: boolean;
      showResults: boolean;
    }
  ): Promise<IUpdateSyncData> {
    return this.put<any>(this.buildUpdateSyncUrl(projectId, options), undefined).then(
      normalizeUpdateSyncData
    );
  }

  bulkInsertLicenseLogReport(data: ILicenseLogEntry[]): Promise<void> {
    const payload = buildTableDataPayload(LicenseLogMeta.tableName, data);

    return this.createPromise(async (resolve, reject) => {
      const response = await fetch(
        LicenseLogMeta.reportBulkInsertUrl,
        {
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": LicenseLogMeta.bulkInsertApiKey,
          },
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        resolve();
        return;
      }

      const errorText = await response.text();
      reject(
        new Error(
          errorText.length > 0 ? errorText : response.statusText || "Bulk insert failed"
        )
      );
      });
  }

  async getLicenseLogReportUrl(): Promise<string> {
    const response = await fetch(LicenseLogMeta.reportExportUrl, {
      headers: {
        "X-API-Key": LicenseLogMeta.exportApiKey,
      },
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        errorText.length > 0 ? errorText : response.statusText || "Report export failed"
      );
    }

    const report = (await response.json()) as IAnalysisFrameworkReport;
    const menuItem = report.reportMenuItems.find((item) =>
      item.reportPage?.elements?.some((element) =>
        element.componentInfo?.columns?.some(
          (column) => column.field?.table?.toLowerCase() === LicenseLogMeta.tableName.toLowerCase()
        )
      )
    );

    if (!menuItem) {
      throw new Error(`No report page is configured for table '${LicenseLogMeta.tableName}'.`);
    }

    return `${LicenseLogMeta.reportViewerUrl}/${encodeURIComponent(
      LicenseLogMeta.reportId
    )}/${encodeURIComponent(menuItem.id)}`;
  }
}

export const AdminViewApi = new AdminViewApiDefault();
